/**
 * How the sky is drawn — the settings that are decided, and the two a
 * learner still chooses.
 *
 * The reference field exposes every reading of the map as a control:
 * which seating, how the columns are told apart, whether rings carry a
 * band, how an unvisited world is drawn, whether the track is dashed,
 * and what the diamond marks show.  Those are design decisions, and
 * here they are MADE rather than offered: one seating (altitudes — the
 * ring is how deep, the sector is which family), the weave for the
 * columns, no bands, a solid track, no marks.  A control that every
 * learner would leave alone is noise on a phone.
 *
 * Two stay live because they answer a question the learner has: what
 * should I look at (the guide) and how is my walk drawn (the links).
 */

import type { PlanetReading, PlanetState } from "./types";

export type Guide = "next" | "frontier" | "cooling" | "none";
export type Links = "chain" | "trail";
/** How an unvisited world is drawn: its rim drawn in, or the whole body veiled. */
export type Unlit = "solid" | "veiled";

export const GUIDES: [Guide, string][] = [
  ["none", "no guidance"],
  ["next", "what's next"],
  ["frontier", "frontier"],
  ["cooling", "what's cooling"],
];

export const LINKS: [Links, string][] = [
  ["chain", "chain"],
  ["trail", "trail"],
];

export const DEFAULT_GUIDE: Guide = "none";
export const DEFAULT_LINKS: Links = "chain";

/**
 * The decided readings, named so the drawing can say which one it is
 * implementing rather than carrying a switch nobody can flip.
 */
export const DRAWING = {
  /** The disc is cut into family wedges; every other one takes a wash. */
  columns: "weave",
  /** The orbits carry no progress band. */
  bands: "none",
  /** The orbit line is a quiet solid thread, not a dashed "unwalked". */
  track: "solid",
  /** No diamond marks on the sky; the card says what wants attention. */
  marks: "none",
} as const;

/**
 * A cold sky wants its worlds drawn in — the learner has nothing to
 * compare them against and a veil would read as a fault.  Once a few
 * worlds have warmed, the untouched ones step back so the walked ones
 * carry the light.  The threshold is the count of worlds with any
 * progress or standing on them.
 */
export const VEIL_FROM = 4;

const PROGRESSED: ReadonlySet<PlanetState> = new Set(["STIRRING", "CLIMBING", "CROSSED", "GRANTED"]);

export function unlitFor(planets: readonly Pick<PlanetReading, "state">[]): Unlit {
  let warmed = 0;
  for (const planet of planets) {
    if (PROGRESSED.has(planet.state)) warmed += 1;
    if (warmed >= VEIL_FROM) return "veiled";
  }
  return "solid";
}
